/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: SystemConfigFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the system module.
----------------------------------------------------------------*/


import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { Settings, Search } from 'lucide-react';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useAuthStore } from '../../store/auth';
import { SystemConfig } from '../../types/system';

// Extracted section components
import { TimeSettingsBox } from './components/TimeSettingsBox';
import { NtpServersBox } from './components/NtpServersBox';
import { MonitoringNotificationsBox } from './components/MonitoringNotificationsBox';

// Hook for live monitoring API stats
import { useServerStats } from './hooks/useServerStats';

// ---------------------------------------------------------------------------
// Default form state
// ---------------------------------------------------------------------------

const DEFAULT_FORM: SystemConfig = {
  id: '',
  name: '',
  serverId: '',
  timezone: 'UTC',
  ntpServers: [
    '0.debian.pool.ntp.org',
    '1.debian.pool.ntp.org',
    '2.debian.pool.ntp.org',
    '3.debian.pool.ntp.org',
  ],
  logLevel: 'INFO',
  maxLogFiles: 10,
  logRetentionDays: 30,
  backupEnabled: true,
  backupFrequency: 'daily',
  backupRetentionDays: 30,
  monitoringEnabled: false,
  alertEmail: '',
  maintenanceMode: false,
  maintenanceMessage: '',
  createdAt: '',
  updatedAt: '',
  currentDate: new Date().toISOString().split('T')[0],
  currentTime: new Date().toTimeString().split(' ')[0],
  clockStatus: 'Synchronized',
  ntpEnabled: true,
  fromAddress: '',
  storageNotifications: false,
  cpuNotifications: false,
  intrusionEmail: '',
  storageThreshold: 70,
  rootStorageNotifications: false,
  varStorageNotifications: false,
  rootStorageThreshold: 70,
  varStorageThreshold: 70,
  rootStorageEmail: '',
  varStorageEmail: '',
  cpuThreshold: 70,
  cpuEmail: '',
  intrusionNotifications: false,
  intrusionThreshold: 5,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SystemConfigFormView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug ?? '';
  const { showSuccess, showError } = useNotifications();

  useFixedActionBar(true);

  const [formData, setFormData] = useState<SystemConfig>(DEFAULT_FORM);
  const [originalData, setOriginalData] = useState<SystemConfig>(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const configLoadedRef = useRef<string | null>(null);

  // Quick-list modal state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListConfigs, setQuickListConfigs] = useState<SystemConfig[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Live server stats (CPU, memory, disk)
  const { serverStats, loadServerStats } = useServerStats(setFormData);

  // Fetch stats on mount
  useEffect(() => { loadServerStats(); }, []);

  // Load quick-list items when the modal opens (lazy, single fetch)
  useEffect(() => {
    if (showQuickList && quickListConfigs.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList]);

  // -- Data fetching ---------------------------------------------------------

  /** Loads system configs for the quick-list modal (up to 10 pages � 100 items) */
  const loadQuickList = async () => {
    if (quickListLoadingRef.current) return;
    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);

    try {
      let allConfigs: SystemConfig[] = [];

      for (let page = 1; page <= 10; page++) {
        const params = new URLSearchParams({ page: String(page), limit: '100', sortBy: 'name', sortOrder: 'asc' });
        const response = await fetch(
          buildApiUrl(`/api/system-misc/configs?${params}`),
          { method: 'GET', headers: getApiHeaders(tenantSlug, user?.id) },
        );

        if (!response.ok) break;

        const result = await response.json();
        const pageData: SystemConfig[] = Array.isArray(result?.data) ? result.data : [];
        allConfigs = [...allConfigs, ...pageData];

        // Stop early if we received less than a full page
        if (pageData.length < 100) break;
      }

      setQuickListConfigs(allConfigs);
    } catch {
      setQuickListConfigs([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  };

  /** Loads telephony server data and populates the form */
  const loadConfigData = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    try {
      const response = await fetch(
        buildApiUrl(`/api/telephony-servers/${id}`),
        { headers: getApiHeaders(tenantSlug, user?.id) },
      );

      if (!response.ok) {
        if (response.status === 404) {
          showError(t('systemMisc.serverNotFound'));
          setTimeout(() => navigate('/admin/system-settings/misc'), 2_000);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const { data: serverData } = await response.json();
      if (!serverData) return;

      const configData: SystemConfig = {
        ...DEFAULT_FORM,
        id: serverData.id,
        name: serverData.name,
        serverId: serverData.id,
        timezone: serverData.timezone ?? 'UTC',
        monitoringEnabled: serverData.monitoringEnabled ?? false,
        createdAt: serverData.createdAt ?? '',
        updatedAt: serverData.updatedAt ?? '',
        server: serverData,
      };

      setFormData(configData);
      setOriginalData(configData);
    } catch (error) {
      showError(t('systemMisc.failedToLoadConfiguration'));
    } finally {
      setLoading(false);
    }
  }, [id, tenantSlug, user?.id, navigate, showError, t]);

  useEffect(() => {
    const key = String(id);
    if (configLoadedRef.current === key) return;
    if (id) {
      configLoadedRef.current = key;
      loadConfigData();
    }
  }, [id, loadConfigData]);

  // -- Form logic -------------------------------------------------------------

  const hasChanges = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(originalData),
    [formData, originalData],
  );

  const handleInputChange = useCallback(
    (field: keyof SystemConfig, value: SystemConfig[keyof SystemConfig]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    },
    [errors],
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name) {
      newErrors.name = t('systemMisc.validation.nameRequired');
    }
    if (!formData.timezone) {
      newErrors.timezone = t('systemMisc.validation.timezoneRequired');
    }
    if (formData.ntpServers.length === 0) {
      newErrors.ntpServers = t('systemMisc.validation.ntpServerRequired');
    }
    if (formData.rootStorageNotifications && !formData.rootStorageEmail) {
      newErrors.rootStorageEmail = t('systemMisc.validation.rootStorageEmailRequired');
    }
    if (formData.varStorageNotifications && !formData.varStorageEmail) {
      newErrors.varStorageEmail = t('systemMisc.validation.varStorageEmailRequired');
    }
    if (formData.cpuNotifications && !formData.cpuEmail) {
      newErrors.cpuEmail = t('systemMisc.validation.cpuEmailRequired');
    }
    if (formData.maintenanceMode && !formData.maintenanceMessage) {
      newErrors.maintenanceMessage = t('systemMisc.validation.maintenanceMessageRequired');
    }

    setErrors(newErrors);
    // Show only the first validation error to avoid flooding the UI
    const firstError = Object.values(newErrors)[0];
    if (firstError) showError(firstError);

    return Object.keys(newErrors).length === 0;
  }, [formData, showError, t]);

  const handleSubmit = useCallback(async (e?: React.FormEvent): Promise<SystemConfig | undefined> => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!validateForm()) return undefined;

    setLoading(true);
    try {
      const response = await fetch(
        buildApiUrl(`/api/telephony-servers/${id}`),
        {
          method: 'PUT',
          headers: getApiHeaders(tenantSlug, user?.id),
          body: JSON.stringify({
            name: formData.name || 'Local Server Configuration',
            timezone: formData.timezone,
            monitoringEnabled: formData.monitoringEnabled,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message ?? `HTTP ${response.status}`);
      }

      showSuccess(t('systemMisc.messages.updateSuccess'));
      setOriginalData({ ...formData });
      return { ...formData };
    } catch (error) {
      showError(error instanceof Error ? error.message : t('systemMisc.failedToSaveConfiguration'));
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [formData, validateForm, id, tenantSlug, user?.id, showSuccess, showError, t]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSubmit,
    listPath: '/admin/system-settings/misc',
    newPath: '/admin/system-settings/misc/new',
    getEditPath: (saved) => `/admin/system-settings/system-misc/${saved.id}`,
    mode: 'edit',
    storageKey: 'system-misc-save-action',
  });

  const handleCancel = useCallback(() => {
    if (originalData) {
      setFormData(originalData);
    }
  }, [originalData]);

  // Client-side filtering for quick-list search
  const filteredQuickList = useMemo(() => {
    if (!quickListSearch.trim()) return quickListConfigs;
    const q = quickListSearch.toLowerCase();
    return quickListConfigs.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.timezone?.toLowerCase().includes(q) ||
        c.logLevel?.toLowerCase().includes(q) ||
        String(c.serverId ?? '').toLowerCase().includes(q),
    );
  }, [quickListConfigs, quickListSearch]);

  // -- Render -----------------------------------------------------------------

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<Settings className="h-5 w-5 text-primary" />}
        title={t('systemMisc.editConfiguration')}
        backButtonText={t('common.list')}
        onBack={() => navigate(-1)}
        actions={[{
          icon: <Search className="h-5 w-5 text-muted-foreground" />,
          tooltip: t('systemMisc.quickList') || 'Quick List',
          onClick: () => setShowQuickList(true),
        }]}
      />

      <div className="mt-8">
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Time + NTP settings (side-by-side on medium+ screens) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TimeSettingsBox formData={formData} onInputChange={handleInputChange} />
            <NtpServersBox formData={formData} onInputChange={handleInputChange} />
          </div>

          {/* Monitoring & Notifications */}
          <MonitoringNotificationsBox
            formData={formData}
            serverStats={serverStats}
            onInputChange={handleInputChange}
            onRefresh={loadServerStats}
          />
        </form>
      </div>

      {/* Quick-List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => { setShowQuickList(false); setQuickListSearch(''); }}
        title={t('systemMisc.quickListTitle', 'Quick List - Select Configuration')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(config) => { setShowQuickList(false); navigate(`/admin/system-settings/misc/${config.id}`); }}
        currentItemId={id ?? null}
        renderItem={(config, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{config.name}</p>
              <p className="text-sm text-muted-foreground">{config.timezone} � {config.logLevel}</p>
              {config.serverId && (
                <p className="text-xs text-muted-foreground mt-1">Server ID: {config.serverId}</p>
              )}
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('systemMisc.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t('systemMisc.searchConfigurations', 'Search configurations...')}
        emptyMessage={t('systemMisc.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('systemMisc.noConfigurationsFound', 'No configurations found')}
        headerIcon={<Settings className="h-5 w-5" />}
        minSearchChars={0}
      />

      {/* Sticky save/cancel bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        isEditMode={true}
        saveText={loading ? t('common.saving') : t('common.save')}
        cancelText={t('common.cancel')}
        saveDisabled={loading}
        isLoading={loading}
        hasChanges={hasChanges}
        useSaveDropdown={true}
        showSave={true}
        saveDropdownStorageKey="system-misc-save-action"
        availableSaveActions={['save', 'save-and-close']}
      />
    </div>
  );
}
